import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Slide,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import ChannelImage from './ChannelImage';
import moment from 'moment';

/**
 * BookingNotification - Displays a compact notification card for new bookings
 * 
 * @param {Object} props
 * @param {Object} booking - Booking object with channel, guest, dates, etc.
 * @param {Function} onClose - Callback when notification is closed
 * @param {Function} onClick - Callback when notification is clicked
 * @param {boolean} open - Whether notification is visible
 */
const BookingNotification = ({ booking, onClose, onClick, open }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!booking || !open) return null;

  const guestName = booking.guest_name || booking.guest?.name || 'Guest';
  const bookingCode = booking.code || booking.booking_code || '';
  const channel = booking.channel_data || booking.channel;
  const channelName = channel?.name || booking.channel || 'Direct';
  const checkInDate = booking.check_in_date ? moment(booking.check_in_date).format('MMM DD') : '';
  const checkOutDate = booking.check_out_date ? moment(booking.check_out_date).format('MMM DD') : '';
  const nights = booking.nights || 0;
  const totalAmount = booking.total_amount || 0;
  const currency = booking.currency || 'INR';

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Theme colors - using rgb(51 81 72 / 60%) similar style
  const notificationBg = isDark
    ? 'rgba(51, 81, 72, 0.6)'
    : 'rgba(51, 81, 72, 0.08)';
  const notificationBorder = isDark
    ? 'rgba(51, 81, 72, 0.4)'
    : 'rgba(51, 81, 72, 0.2)';
  const textPrimary = isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)';
  const textSecondary = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={4}
        onClick={onClick}
        sx={{
          position: 'relative',
          width: 320,
          height: 100,
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: notificationBg,
          border: `1px solid ${notificationBorder}`,
          backdropFilter: 'blur(8px)',
          '&:hover': {
            transform: 'translateX(-2px)',
            boxShadow: theme.shadows[8],
            borderColor: isDark ? 'rgba(51, 81, 72, 0.6)' : 'rgba(51, 81, 72, 0.3)',
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 10,
            width: 20,
            height: 20,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.2),
            },
            '& .MuiSvgIcon-root': {
              fontSize: '0.875rem',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content - Compact Layout */}
        <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Main Content Row */}
          <Box sx={{ display: 'flex', gap: 1, flex: 1, minHeight: 0 }}>
            {/* Left Side: Channel Image */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {channel ? (
                <ChannelImage channel={channel} size={36} />
              ) : (
                <ChannelImage isPMS={true} size={36} />
              )}
            </Box>

            {/* Right Side: Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {/* Guest Name */}
              <Typography
                component="div"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: textPrimary,
                  mb: 0.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {guestName}
              </Typography>

              {/* Dates and Amount Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                <CalendarIcon
                  sx={{
                    fontSize: 13,
                    color: textSecondary,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  component="div"
                  sx={{
                    fontSize: '0.7rem',
                    color: textSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {checkInDate} → {checkOutDate}
                  {nights > 0 && ` (${nights}N)`}
                </Typography>
                
                {/* Amount Chip - Yellow */}
                {totalAmount > 0 && (
                  <Chip
                    label={formatCurrency(totalAmount)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: isDark ? 'rgba(255, 193, 7, 0.25)' : 'rgba(255, 193, 7, 0.2)',
                      color: isDark ? '#FFC107' : '#F57C00',
                      border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.4)' : 'rgba(255, 193, 7, 0.3)'}`,
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      },
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Bottom Row: Channel, Status, and Booking Code - Outside content box */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.5, width: '100%' }}>
            {/* Left side: Channel and Status - Start from absolute leftmost */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {/* Channel Chip */}
              <Chip
                label={channelName}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: textPrimary,
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
                  '& .MuiChip-label': {
                    padding: '0 6px',
                  },
                }}
              />

              {/* Status Chip */}
              {booking.status && (
                <Chip
                  label={booking.status.replace(/_/g, ' ').toUpperCase()}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    backgroundColor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.main,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    '& .MuiChip-label': {
                      padding: '0 6px',
                    },
                  }}
                />
              )}
            </Box>

            {/* Right side: Booking Code */}
            {bookingCode && (
              <Typography
                component="div"
                sx={{
                  fontSize: '0.6rem',
                  color: textSecondary,
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
                title={`Booking Code: ${bookingCode}`}
              >
                {bookingCode}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default BookingNotification;
